import { makeAutoObservable } from 'mobx'
import CalendarEventService from './../../../shared/services/CalendarEvent.service'
import moment from 'moment-timezone'

/**
 * USABG Events calendar ID from the database.
 * @constant {number}
 */
const USABG_EVENTS_CALENDAR_ID = 21

/**
 * Default color when no calendar color is set.
 * Google Calendar Blue.
 */
const DEFAULT_EVENT_COLOR = '#4285F4'

/**
 * EventStore
 *
 * MobX store for managing USABG events data.
 * Fetches events from the calendar_events table and provides
 * them separated into upcoming and past events.
 */
class EventStore {
  isLoading = false
  isFetching = false
  error = null
  events = []

  constructor() {
    makeAutoObservable(this)
  }

  get upcomingEvents() {
    const today = moment().startOf('day')
    return this.events.filter(event => {
      const eventEnd = moment.utc(event.endsAt).startOf('day')
      return eventEnd.isSameOrAfter(today)
    })
  }

  get pastEvents() {
    const today = moment().startOf('day')
    return this.events
      .filter(event => {
        const eventEnd = moment.utc(event.endsAt).startOf('day')
        return eventEnd.isBefore(today)
      })
      .reverse()
  }

  init = async () => {
    this.isLoading = true
    await this.fetchEvents()
    this.isLoading = false
  }

  fetchEvents = async () => {
    this.isFetching = true
    this.error = null

    const startDate = moment().subtract(1, 'year').format('YYYY-MM-DD')
    const endDate = moment().add(1, 'year').format('YYYY-MM-DD')

    try {
      const eventData = (await CalendarEventService.getByCalendarId(USABG_EVENTS_CALENDAR_ID, startDate, endDate)) || []

      this.events = eventData.map(event => {
        // Parse as UTC, then convert to event's timezone for display
        const eventTz = event.tz || 'America/New_York'
        const startMoment = moment.utc(event.starts_at).tz(eventTz)
        const endMoment = moment.utc(event.ends_at).tz(eventTz)
        const isMultiDay = !startMoment.isSame(endMoment, 'day')

        return {
          id: event.id,
          eventId: event.event_id,
          calendarId: event.calendar_id,
          summary: event.summary,
          description: event.description,
          // event_color is the individual event's color (hex format)
          // cal_color is the calendar's default color as fallback
          eventColorHex: event.event_color || event.cal_color || DEFAULT_EVENT_COLOR,
          startsAt: event.starts_at,
          endsAt: event.ends_at,
          timezone: eventTz,
          isMultiDay,
          startDate: startMoment.format('YYYY-MM-DD'),
          endDate: endMoment.format('YYYY-MM-DD'),
          displayStartDate: startMoment.format('MMM D, YYYY'),
          displayEndDate: endMoment.format('MMM D, YYYY'),
          displayStartTime: startMoment.format('h:mm A'),
          displayEndTime: endMoment.format('h:mm A'),
          dayOfWeek: startMoment.format('dddd'),
          startDay: startMoment.format('D'),
          startMonth: startMoment.format('MMM'),
        }
      })

      this.events.sort((a, b) => moment(a.startsAt).diff(moment(b.startsAt)))
    } catch (ex) {
      console.error('Failed to fetch events:', ex)
      this.error = 'Failed to load events. Please try again.'
      this.events = []
    } finally {
      this.isFetching = false
    }
  }

  deleteEvent = async eventId => {
    await CalendarEventService.delete(eventId)
    await this.fetchEvents()
  }

  reset = () => {
    this.isLoading = false
    this.isFetching = false
    this.error = null
    this.events = []
  }
}

export default new EventStore()
